import { env } from '@hub/config/runtime';
import { type PaymentMethodOverrides, preloadBillwerkScript } from '@platform/payment';

export interface PaymentSdkConfig {
  publicKey: string;
  billwerkSdkUrl: string;
  paymentMethodOverrides: PaymentMethodOverrides;
}

// Stable identity: `useCreditCardForm` keys its init effect on this and would rebuild the hosted iframe.
const PAYPAL_OVERRIDE = { payPal: env.billwerkPayPalBearer };

/**
 * The Billwerk credentials as `PaymentProvider` wants them, or null when the frontend config carries none.
 * Mounting the provider without them fails the SDK load silently, so a surface checks this first.
 */
export function paymentSdkConfig(): PaymentSdkConfig | null {
  const publicKey = env.billwerkPublicKey;
  const billwerkSdkUrl = env.billwerkSdkUrl;

  return publicKey && billwerkSdkUrl ? { publicKey, billwerkSdkUrl, paymentMethodOverrides: PAYPAL_OVERRIDE } : null;
}

/**
 * Starts fetching the Billwerk SDK before a surface mounts `PaymentProvider`, so the download overlaps
 * the payment chunk loads instead of queueing behind them. No-op when the config carries no credentials.
 */
export function preloadPaymentSdk(): void {
  const url = paymentSdkConfig()?.billwerkSdkUrl;
  if (!url) {
    return;
  }

  try {
    preloadBillwerkScript(url);
  } catch {
    // Only a rejected `billwerkSdkUrl` throws here, and `configureBillwerkSdkUrl` throws again on it when
    // `PaymentProvider` mounts — where RouteErrorComponent reports it against a page the user actually
    // opened. Reporting from this speculative path would double-count and fire on mere hover.
  }
}
